import { createScopedErrors, registerCodes, reportInvalidInput } from '@norkron/web-errors';
import { registerResponsiveContract } from '@norkron/web-conformance';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { z } from 'zod';
import { memo, useState, useEffect } from 'react';
import { useDisclosure } from '@norkron/web-a11y';
import { useThemeVar } from '@norkron/web-themer/react';
import { ErrorBoundary } from '@norkron/web-error-boundary';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
export { Skeleton, SkeletonAvatar, SkeletonCard, SkeletonList, SkeletonTable, SkeletonText } from '@norkron/web-skeleton';

// src/error-codes.ts
var STATES_LIB = "@norkron/web-states";
var STATES_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.states");
var { warn, panic } = createScopedErrors(STATES_LIB, STATES_TOKEN);
try {
  registerCodes(STATES_LIB, {
    STATES_INVALID_PROPS: {
      description: "A state-primitive prop violated its contract \u2014 e.g. `onRetry` was not a function, or `actionContent` resolved to a non-renderable value. The component falls back to a safe render path; the warning surfaces the misuse.",
      severity: "warning",
      docsAnchor: "states/invalid-props",
      exampleFix: "Pass a function for `onRetry` (or omit it). For action slots, pass a single ReactNode (button, link, fragment) \u2014 not a primitive other than string/number/null."
    },
    WEB_STATES_INVALID_INPUT: {
      description: "A public input to @norkron/web-states failed zod validation. Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib.",
      severity: "warning",
      docsAnchor: "https://norkron.dev/web-errors/WEB_INVALID_INPUT_SHAPE",
      exampleFix: "Match the per-export schema (EmptyStatePropsSchema, LoadingStatePropsSchema). The fieldPath shows which prop failed and what was expected."
    },
    ST_ICON_OVER_BOUND: {
      description: "selfCheck finding: a state-primitive icon exceeds the recommended visual bounds for empty/loading/error states; harms layout and readability.",
      severity: "info",
      docsAnchor: "web-states/icon-over-bound",
      exampleFix: "Constrain the icon to the documented size (e.g. 48px\u201372px) or use a smaller variant."
    },
    ST_MISSING_ACTION: {
      description: "selfCheck finding: a non-loading state primitive (empty/error) has no actionable affordance; users have no recovery path.",
      severity: "warning",
      docsAnchor: "web-states/missing-action",
      exampleFix: "Provide an action slot (retry button, link to docs, primary CTA) so users can recover."
    },
    ST_SELFCHECK_BUDGET_EXCEEDED: {
      description: "selfCheck info finding: time budget exceeded during the audit; partial findings returned.",
      severity: "info",
      docsAnchor: "web-states/selfcheck-budget-exceeded"
    },
    ST_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck info finding: the self-check threw internally and was caught; reported so callers can detect failures without halting the conformance run.",
      severity: "info",
      docsAnchor: "web-states/selfcheck-internal-error"
    }
  });
} catch {
}
var RESPONSIVE_CONTRACT = {
  lib: "@norkron/web-states",
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    xs: "full",
    sm: "full",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [],
  rwdSelfCheckCodes: []
};
registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var LIB_ID = "@norkron/web-states";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function selfCheck(root, opts) {
  const start = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const instances = Array.from(
      root.querySelectorAll('[data-norkron-lib^="@norkron/web-states"]')
    );
    for (const inst of instances) {
      const now = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
      if (now - start > safeOpts.budgetMs) {
        findings.push({
          libId: LIB_ID,
          code: "ST_SELFCHECK_BUDGET_EXCEEDED",
          category: "perf",
          severity: "info",
          message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms; partial results returned.`,
          extras: { budgetMs: safeOpts.budgetMs, elapsed: now - start }
        });
        break;
      }
      const variant = inst.getAttribute("data-states-variant");
      const containerWidth = inst.clientWidth;
      if (containerWidth > 0) {
        const svgs = inst.querySelectorAll("svg");
        for (const svg of svgs) {
          const svgWidth = svg.clientWidth ?? 0;
          if (svgWidth > containerWidth * 1.5) {
            findings.push({
              libId: LIB_ID,
              code: "ST_ICON_OVER_BOUND",
              category: "layout",
              severity: "major",
              message: `Icon (${svgWidth}px) inside state component overflows its container (${containerWidth}px) by more than 50%.`,
              selector: selectorFor(svg),
              suggestedFix: "Constrain the icon via CSS `max-width: 100%` or set an explicit `width`/`height` on the SVG.",
              extras: { svgWidth, containerWidth, variant }
            });
          }
        }
      }
      if (variant === "empty") {
        const isSearchEmpty = inst.getAttribute("data-search") === "true";
        const hasAction = inst.getAttribute("data-states-has-action") === "true";
        if (!hasAction && !isSearchEmpty) {
          findings.push({
            libId: LIB_ID,
            code: "ST_MISSING_ACTION",
            category: "flow",
            severity: "minor",
            message: "EmptyState rendered without an action slot \u2014 users have no suggested next step.",
            selector: selectorFor(inst),
            suggestedFix: "Pass an `action` (or `onRetry`) prop pointing the user at the next step (filter reset, retry, create new item, etc)."
          });
        }
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "ST_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}

// src/version.ts
var LIB_VERSION = "0.6.5";
var EmptyStatePropsSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  icon: z.unknown().optional(),
  illustration: z.unknown().optional(),
  action: z.unknown().optional(),
  actionContent: z.unknown().optional(),
  actions: z.unknown().optional(),
  onRetry: z.unknown().refine((v) => v === void 0 || typeof v === "function", {
    message: "expected function or undefined"
  }).optional(),
  retryLabel: z.string().optional(),
  className: z.string().optional(),
  live: z.enum(["polite", "assertive", "off"]).optional(),
  primaryAction: z.unknown().optional(),
  secondaryAction: z.unknown().optional(),
  tone: z.enum(["neutral", "info"]).optional(),
  size: z.enum(["sm", "md", "lg", "xl"]).optional(),
  search: z.object({
    query: z.string(),
    onClear: z.unknown().refine((v) => v === void 0 || typeof v === "function", {
      message: "expected function or undefined"
    }).optional(),
    clearLabel: z.string().optional()
  }).optional()
});
var LoadingStatePropsSchema = z.object({
  lines: z.number().int().positive().optional(),
  className: z.string().optional(),
  label: z.string().optional(),
  variant: z.enum([
    "default",
    "inline",
    "full-page",
    "spinner",
    "dots",
    "bar",
    "skeleton"
  ]).optional(),
  pulseStaggerMs: z.number().nonnegative().optional(),
  kind: z.enum(["skeleton-lines", "skeleton-card", "spinner"]).optional(),
  size: z.enum(["sm", "md", "lg", "xl"]).optional(),
  fullPage: z.boolean().optional()
});
var ErrorStatePropsSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  icon: z.unknown().optional(),
  illustration: z.unknown().optional(),
  actions: z.unknown().optional(),
  onRetry: z.unknown().refine((v) => v === void 0 || typeof v === "function", {
    message: "expected function or undefined"
  }).optional(),
  retryLabel: z.string().optional(),
  className: z.string().optional(),
  live: z.enum(["polite", "assertive", "off"]).optional(),
  size: z.enum(["sm", "md", "lg", "xl"]).optional()
});

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}

// src/validate-input.ts
function validateEmptyStateProps(props) {
  const parsed = EmptyStatePropsSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({
      lib: STATES_LIB,
      libToken: STATES_TOKEN,
      fn: "EmptyState",
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}
function validateLoadingStateProps(props) {
  const parsed = LoadingStatePropsSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({
      lib: STATES_LIB,
      libToken: STATES_TOKEN,
      fn: "LoadingState",
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}
function validateErrorStateProps(props) {
  const parsed = ErrorStatePropsSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({
      lib: STATES_LIB,
      libToken: STATES_TOKEN,
      fn: "ErrorState",
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}
var BASE_PROPS = {
  viewBox: "0 0 120 120",
  width: "120",
  height: "120",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: false
};
var ACCENT = "var(--state-illustration-accent, currentColor)";
function wrap(key, body) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      ...BASE_PROPS,
      "data-testid": `state-illustration-${key}`,
      "data-illustration": key,
      children: body
    }
  );
}
function EmptyFolderIllustration() {
  return wrap(
    "empty-folder",
    /* @__PURE__ */ jsxs("g", { children: [
      /* @__PURE__ */ jsx("path", { d: "M20 40h28l8 8h44v44a4 4 0 0 1-4 4H24a4 4 0 0 1-4-4V40z" }),
      /* @__PURE__ */ jsx("path", { d: "M60 64v16", stroke: ACCENT }),
      /* @__PURE__ */ jsx("circle", { cx: "60", cy: "86", r: "2", fill: ACCENT, stroke: ACCENT })
    ] })
  );
}
function EmptySearchIllustration() {
  return wrap(
    "empty-search",
    /* @__PURE__ */ jsxs("g", { children: [
      /* @__PURE__ */ jsx("circle", { cx: "52", cy: "52", r: "24" }),
      /* @__PURE__ */ jsx("path", { d: "M70 70l20 20", stroke: ACCENT }),
      /* @__PURE__ */ jsx("path", { d: "M44 52h16" })
    ] })
  );
}
function EmptyInboxIllustration() {
  return wrap(
    "empty-inbox",
    /* @__PURE__ */ jsxs("g", { children: [
      /* @__PURE__ */ jsx("path", { d: "M20 80V40h80v40" }),
      /* @__PURE__ */ jsx("path", { d: "M20 80h28l4 8h16l4-8h28v16a4 4 0 0 1-4 4H24a4 4 0 0 1-4-4V80z" }),
      /* @__PURE__ */ jsx("path", { d: "M40 56h40", stroke: ACCENT })
    ] })
  );
}
function EmptyDataIllustration() {
  return wrap(
    "empty-data",
    /* @__PURE__ */ jsxs("g", { children: [
      /* @__PURE__ */ jsx("path", { d: "M20 100h80" }),
      /* @__PURE__ */ jsx("rect", { x: "30", y: "70", width: "12", height: "30" }),
      /* @__PURE__ */ jsx("rect", { x: "54", y: "55", width: "12", height: "45" }),
      /* @__PURE__ */ jsx("rect", { x: "78", y: "80", width: "12", height: "20", stroke: ACCENT })
    ] })
  );
}
function ErrorServerIllustration() {
  return wrap(
    "error-server",
    /* @__PURE__ */ jsxs("g", { children: [
      /* @__PURE__ */ jsx("rect", { x: "24", y: "36", width: "72", height: "20", rx: "2" }),
      /* @__PURE__ */ jsx("rect", { x: "24", y: "64", width: "72", height: "20", rx: "2" }),
      /* @__PURE__ */ jsx("circle", { cx: "36", cy: "46", r: "2", fill: ACCENT, stroke: ACCENT }),
      /* @__PURE__ */ jsx("circle", { cx: "36", cy: "74", r: "2", fill: ACCENT, stroke: ACCENT }),
      /* @__PURE__ */ jsx("path", { d: "M20 96L100 24", stroke: ACCENT })
    ] })
  );
}
function Error404Illustration() {
  return wrap(
    "error-404",
    /* @__PURE__ */ jsxs("g", { children: [
      /* @__PURE__ */ jsx("path", { d: "M30 70l16-30v50" }),
      /* @__PURE__ */ jsx("path", { d: "M30 60h32" }),
      /* @__PURE__ */ jsx("circle", { cx: "74", cy: "60", r: "12", stroke: ACCENT }),
      /* @__PURE__ */ jsx("path", { d: "M88 70l16-30v50" }),
      /* @__PURE__ */ jsx("path", { d: "M88 60h32" })
    ] })
  );
}
function Error403Illustration() {
  return wrap(
    "error-403",
    /* @__PURE__ */ jsxs("g", { children: [
      /* @__PURE__ */ jsx("rect", { x: "36", y: "56", width: "48", height: "36", rx: "4" }),
      /* @__PURE__ */ jsx("path", { d: "M48 56V44a12 12 0 0 1 24 0v12" }),
      /* @__PURE__ */ jsx("circle", { cx: "60", cy: "72", r: "3", fill: ACCENT, stroke: ACCENT }),
      /* @__PURE__ */ jsx("path", { d: "M60 75v8", stroke: ACCENT })
    ] })
  );
}
function ErrorNetworkIllustration() {
  return wrap(
    "error-network",
    /* @__PURE__ */ jsxs("g", { children: [
      /* @__PURE__ */ jsx("path", { d: "M30 60l16-16 12 12-16 16z" }),
      /* @__PURE__ */ jsx("path", { d: "M62 76l12 12 16-16-12-12z" }),
      /* @__PURE__ */ jsx("path", { d: "M44 30v10M76 90v-10", stroke: ACCENT }),
      /* @__PURE__ */ jsx("path", { d: "M48 60l8 8" })
    ] })
  );
}
function OfflineCloudIllustration() {
  return wrap(
    "offline-cloud",
    /* @__PURE__ */ jsxs("g", { children: [
      /* @__PURE__ */ jsx("path", { d: "M36 76h48a12 12 0 0 0 0-24 16 16 0 0 0-31-4 12 12 0 0 0-17 28z" }),
      /* @__PURE__ */ jsx("path", { d: "M24 24l72 72", stroke: ACCENT })
    ] })
  );
}
function MaintenanceToolsIllustration() {
  return wrap(
    "maintenance-tools",
    /* @__PURE__ */ jsxs("g", { children: [
      /* @__PURE__ */ jsx("path", { d: "M28 32l40 40-8 8-40-40z" }),
      /* @__PURE__ */ jsx("circle", { cx: "30", cy: "34", r: "6", stroke: ACCENT }),
      /* @__PURE__ */ jsx("path", { d: "M92 28l-32 32 8 8 32-32z", stroke: ACCENT }),
      /* @__PURE__ */ jsx("rect", { x: "84", y: "20", width: "12", height: "12", rx: "2", stroke: ACCENT })
    ] })
  );
}
var REGISTRY = {
  "empty-folder": EmptyFolderIllustration,
  "empty-search": EmptySearchIllustration,
  "empty-inbox": EmptyInboxIllustration,
  "empty-data": EmptyDataIllustration,
  "error-server": ErrorServerIllustration,
  "error-404": Error404Illustration,
  "error-403": Error403Illustration,
  "error-network": ErrorNetworkIllustration,
  "offline-cloud": OfflineCloudIllustration,
  "maintenance-tools": MaintenanceToolsIllustration
};
var STATE_ILLUSTRATION_KEYS = Object.freeze(
  Object.keys(REGISTRY).sort()
);
function isStateIllustrationKey(key) {
  return typeof key === "string" && key in REGISTRY;
}
function renderStateIllustration(key) {
  const Comp = REGISTRY[key];
  return Comp ? /* @__PURE__ */ jsx(Comp, {}) : null;
}
var EmptyState = memo(function EmptyState2(props) {
  validateEmptyStateProps(props);
  const {
    title,
    description = "Try adjusting your search or filter criteria.",
    icon,
    illustration,
    action,
    actionContent,
    actions,
    onRetry,
    retryLabel = "Try again",
    className,
    live = "off",
    primaryAction,
    secondaryAction,
    tone = "neutral",
    size = "md",
    search
  } = props;
  const resolvedTitle = title ?? (search ? `No results found for "${search.query}"` : "No results found");
  const bg = useThemeVar(
    "--empty-state-bg",
    "var(--norkron-surface-base, var(--color-bg, var(--theme-bg, transparent)))"
  );
  const fg = useThemeVar(
    "--empty-state-fg",
    "var(--norkron-color-fg-base, var(--color-fg, var(--theme-fg, currentColor)))"
  );
  const action_disclosure = useDisclosure({ defaultOpen: true });
  if (onRetry !== void 0 && typeof onRetry !== "function") {
    try {
      warn({
        fn: "EmptyState",
        code: "STATES_INVALID_PROPS",
        message: "EmptyState received a non-function `onRetry`; built-in retry button will be suppressed.",
        prop: "onRetry",
        received: onRetry,
        expected: "a function () => void (or omit the prop)",
        dedupKey: "EmptyState:onRetry-not-a-function"
      });
    } catch {
    }
  }
  let resolvedIllustration = null;
  if (typeof illustration === "string") {
    if (isStateIllustrationKey(illustration)) {
      resolvedIllustration = renderStateIllustration(illustration);
    } else {
      try {
        warn({
          fn: "EmptyState",
          code: "STATES_INVALID_PROPS",
          message: `EmptyState received unknown illustration key "${illustration}"; illustration slot will collapse.`,
          prop: "illustration",
          received: illustration,
          expected: "a known StateIllustration key or a ReactNode",
          dedupKey: `EmptyState:unknown-illustration:${illustration}`
        });
      } catch {
      }
    }
  } else if (illustration != null) {
    resolvedIllustration = illustration;
  }
  const rootClass = className ? `norkron-empty-state ${className}` : "norkron-empty-state";
  const actionsArray = actions ? Array.isArray(actions) ? actions.filter((a) => a != null) : [actions] : null;
  const hasActionsArray = actionsArray != null && actionsArray.length > 0;
  const hasNewActionSlots = !hasActionsArray && (primaryAction != null || secondaryAction != null);
  const explicitAction = !hasActionsArray && !hasNewActionSlots ? action ?? actionContent : null;
  const safeOnRetry = typeof onRetry === "function" ? onRetry : void 0;
  const safeOnClear = typeof search?.onClear === "function" ? search.onClear : void 0;
  const clearNode = !hasActionsArray && !hasNewActionSlots && !explicitAction && search && safeOnClear ? /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: safeOnClear,
      "data-testid": "empty-state__clear-search",
      className: "norkron-empty-state__retry",
      children: search.clearLabel ?? "Clear search"
    }
  ) : null;
  const retryNode = !hasActionsArray && !hasNewActionSlots && !explicitAction && !clearNode && safeOnRetry ? /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: safeOnRetry,
      "data-testid": "empty-state__retry",
      className: "norkron-empty-state__retry",
      children: retryLabel
    }
  ) : null;
  const composedActionsArray = hasActionsArray ? /* @__PURE__ */ jsx(Fragment, { children: actionsArray.map((a, i) => /* @__PURE__ */ jsx(
    "span",
    {
      "data-testid": `empty-state-action-item-${i}`,
      className: "norkron-empty-state__action-item",
      children: a
    },
    i
  )) }) : null;
  const composedActions = hasNewActionSlots ? /* @__PURE__ */ jsxs(Fragment, { children: [
    primaryAction ? /* @__PURE__ */ jsx(
      "span",
      {
        "data-testid": "empty-state-action-primary",
        className: "norkron-empty-state__action-primary",
        children: primaryAction
      }
    ) : null,
    secondaryAction ? /* @__PURE__ */ jsx(
      "span",
      {
        "data-testid": "empty-state-action-secondary",
        className: "norkron-empty-state__action-secondary",
        children: secondaryAction
      }
    ) : null
  ] }) : null;
  const finalAction = composedActionsArray ?? composedActions ?? explicitAction ?? clearNode ?? retryNode;
  const liveProps = live === "off" ? null : { role: "status", "aria-live": live };
  const themeStyle = {
    "--empty-state-bg": bg,
    "--empty-state-fg": fg
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ...liveProps ?? {},
      className: rootClass,
      "data-testid": "empty-state",
      "data-norkron-lib": `@norkron/web-states@${LIB_VERSION}`,
      "data-states-has-action": finalAction ? "true" : "false",
      "data-states-variant": "empty",
      "data-tone": tone,
      "data-size": size,
      "data-search": search ? "true" : "false",
      style: themeStyle,
      children: [
        resolvedIllustration ? /* @__PURE__ */ jsx(
          "div",
          {
            "data-testid": "empty-state-illustration",
            className: "norkron-empty-state__illustration",
            "aria-hidden": "true",
            children: resolvedIllustration
          }
        ) : illustration != null ? null : icon ? /* @__PURE__ */ jsx(
          "div",
          {
            "data-testid": "empty-state-icon",
            className: "norkron-empty-state__icon",
            "aria-hidden": "true",
            children: icon
          }
        ) : null,
        /* @__PURE__ */ jsx("h3", { className: "norkron-empty-state__title", children: resolvedTitle }),
        /* @__PURE__ */ jsx("p", { className: "norkron-empty-state__description", children: description }),
        finalAction && action_disclosure.open ? /* @__PURE__ */ jsx("div", { "data-testid": "empty-state-action", className: "norkron-empty-state__action", children: /* @__PURE__ */ jsx(ErrorBoundary, { fallback: null, children: finalAction }) }) : null
      ]
    }
  );
});
var VARIANT_CLASS = {
  default: "",
  inline: "norkron-loading-state--inline",
  "full-page": "norkron-loading-state--full-page",
  spinner: "",
  dots: "",
  bar: "",
  skeleton: ""
};
function resolveKind(variant, explicitKind) {
  if (explicitKind) return explicitKind;
  if (variant === "spinner") return "spinner";
  if (variant === "dots") return "dots";
  if (variant === "bar") return "bar";
  if (variant === "skeleton") return "skeleton-lines";
  return "skeleton-lines";
}
var LoadingState = memo(function LoadingState2(props) {
  validateLoadingStateProps(props);
  const {
    lines = 5,
    className,
    label = "Loading",
    variant = "default",
    pulseStaggerMs = 80,
    kind: explicitKind,
    size = "md",
    fullPage = false
  } = props;
  const safeLines = Math.max(1, Math.floor(lines));
  const safeStagger = Number.isFinite(pulseStaggerMs) ? Math.max(0, pulseStaggerMs) : 0;
  const kind = resolveKind(variant, explicitKind);
  const variantClass = VARIANT_CLASS[variant];
  const kindClass = kind === "skeleton-lines" ? "" : `norkron-loading-state--${kind}`;
  const fullPageClass = fullPage ? "norkron-loading-state--full-page" : "";
  const isFullPage = fullPage || variant === "full-page";
  const rootClass = [
    "norkron-loading-state",
    kindClass,
    variantClass,
    fullPageClass,
    className
  ].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "status",
      "aria-busy": "true",
      "aria-live": "polite",
      "aria-label": label,
      className: rootClass,
      "data-testid": "loading-state",
      "data-norkron-lib": `@norkron/web-states@${LIB_VERSION}`,
      "data-states-variant": "loading",
      "data-variant": variant,
      "data-kind": kind,
      "data-size": size,
      "data-full-page": fullPage ? "true" : "false",
      ...isFullPage ? {
        "data-allow-viewport-overflow": "true",
        "data-allow-flex-nowrap-overflow": "true",
        "data-allow-overflow": "true"
      } : {},
      children: kind === "spinner" ? /* @__PURE__ */ jsx(
        "div",
        {
          className: "norkron-loading-state__spinner",
          "data-testid": "loading-state-spinner",
          "aria-hidden": "true"
        }
      ) : kind === "dots" ? /* @__PURE__ */ jsxs(
        "div",
        {
          className: "norkron-loading-state__dots",
          "data-testid": "loading-state-dots",
          "aria-hidden": "true",
          children: [
            /* @__PURE__ */ jsx("span", { className: "norkron-loading-state__dot" }),
            /* @__PURE__ */ jsx("span", { className: "norkron-loading-state__dot" }),
            /* @__PURE__ */ jsx("span", { className: "norkron-loading-state__dot" })
          ]
        }
      ) : kind === "bar" ? /* @__PURE__ */ jsx(
        "div",
        {
          className: "norkron-loading-state__bar",
          "data-testid": "loading-state-bar",
          "aria-hidden": "true",
          children: /* @__PURE__ */ jsx("div", { className: "norkron-loading-state__bar-indeterminate" })
        }
      ) : kind === "skeleton-card" ? /* @__PURE__ */ jsxs(
        "div",
        {
          className: "norkron-loading-state__card",
          "data-testid": "loading-state-card",
          "aria-hidden": "true",
          children: [
            /* @__PURE__ */ jsx("div", { className: "norkron-loading-state__card-header" }),
            /* @__PURE__ */ jsx("div", { className: "norkron-loading-state__card-body" }),
            /* @__PURE__ */ jsx("div", { className: "norkron-loading-state__card-body" })
          ]
        }
      ) : Array.from({ length: safeLines }, (_, i) => /* @__PURE__ */ jsx(
        "div",
        {
          className: "norkron-skeleton",
          "aria-hidden": "true",
          "data-testid": "loading-skeleton-line",
          style: safeStagger > 0 ? { animationDelay: `${i * safeStagger}ms` } : void 0,
          children: /* @__PURE__ */ jsx(
            "div",
            {
              className: "norkron-skeleton__bar",
              "data-testid": "loading-skeleton-bar",
              style: {
                width: `${65 + (i * 17 + 7) % 30}%`,
                ...safeStagger > 0 ? { animationDelay: `${i * safeStagger}ms` } : {}
              }
            }
          )
        },
        i
      ))
    }
  );
});
var ErrorState = memo(function ErrorState2(props) {
  validateErrorStateProps(props);
  const {
    title = "Something went wrong",
    description,
    icon,
    illustration,
    actions,
    onRetry,
    retryLabel = "Try again",
    className,
    live = "polite",
    size = "md"
  } = props;
  const actionsArray = actions ? Array.isArray(actions) ? actions.filter((a) => a != null) : null : null;
  const renderedActions = actionsArray && actionsArray.length > 0 ? actionsArray.map((a, i) => /* @__PURE__ */ jsx(
    "span",
    {
      "data-testid": `error-state-action-item-${i}`,
      className: "norkron-error-state__action-item",
      children: a
    },
    i
  )) : actions;
  if (onRetry !== void 0 && typeof onRetry !== "function") {
    try {
      warn({
        fn: "ErrorState",
        code: "STATES_INVALID_PROPS",
        message: "ErrorState received a non-function `onRetry`; built-in retry button will be suppressed.",
        prop: "onRetry",
        received: onRetry,
        expected: "a function () => void (or omit the prop)",
        dedupKey: "ErrorState:onRetry-not-a-function"
      });
    } catch {
    }
  }
  const safeOnRetry = typeof onRetry === "function" ? onRetry : void 0;
  const retryNode = !actions && safeOnRetry ? /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: safeOnRetry,
      "data-testid": "error-state__retry",
      className: "norkron-error-state__retry",
      children: /* @__PURE__ */ jsx("span", { className: "norkron-error-state__retry-label", children: retryLabel })
    }
  ) : null;
  const finalActions = renderedActions ?? retryNode;
  let resolvedIllustration = null;
  if (typeof illustration === "string") {
    if (isStateIllustrationKey(illustration)) {
      resolvedIllustration = renderStateIllustration(illustration);
    } else {
      try {
        warn({
          fn: "ErrorState",
          code: "STATES_INVALID_PROPS",
          message: `ErrorState received unknown illustration key "${illustration}"; illustration slot will collapse.`,
          prop: "illustration",
          received: illustration,
          expected: "a known StateIllustration key or a ReactNode",
          dedupKey: `ErrorState:unknown-illustration:${illustration}`
        });
      } catch {
      }
    }
  } else if (illustration != null) {
    resolvedIllustration = illustration;
  }
  const rootClass = className ? `norkron-error-state ${className}` : "norkron-error-state";
  const liveProps = live === "off" ? null : { role: "alert", "aria-live": live };
  const themeStyle = {
    // Surface the catalog danger tokens as the local accent so the
    // stylesheet does not have to hard-code them. Consumers can still
    // override `--error-state-accent` to retheme.
    "--error-state-accent": "var(--norkron-state-danger, var(--color-danger))",
    "--error-state-accent-fg": "var(--norkron-text-on-accent, var(--color-fg-on-danger, var(--color-danger-fg)))"
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ...liveProps ?? {},
      className: rootClass,
      "data-testid": "error-state",
      "data-norkron-lib": `@norkron/web-states@${LIB_VERSION}`,
      "data-states-has-action": finalActions ? "true" : "false",
      "data-states-variant": "error",
      "data-size": size,
      style: themeStyle,
      children: [
        resolvedIllustration ? /* @__PURE__ */ jsx(
          "div",
          {
            "data-testid": "error-state-illustration",
            className: "norkron-error-state__illustration",
            "aria-hidden": "true",
            children: resolvedIllustration
          }
        ) : illustration != null ? null : icon ? /* @__PURE__ */ jsx(
          "div",
          {
            "data-testid": "error-state-icon",
            className: "norkron-error-state__icon",
            "aria-hidden": "true",
            children: icon
          }
        ) : null,
        /* @__PURE__ */ jsx("h3", { className: "norkron-error-state__title", children: title }),
        description ? /* @__PURE__ */ jsx("p", { className: "norkron-error-state__description", children: description }) : null,
        finalActions ? /* @__PURE__ */ jsx("div", { "data-testid": "error-state-actions", className: "norkron-error-state__actions", children: /* @__PURE__ */ jsx(ErrorBoundary, { fallback: null, children: finalActions }) }) : null
      ]
    }
  );
});
var OfflineStatePropsSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  icon: z.unknown().optional(),
  illustration: z.unknown().optional(),
  actions: z.unknown().optional(),
  className: z.string().optional(),
  live: z.enum(["polite", "assertive", "off"]).optional(),
  size: z.enum(["sm", "md", "lg", "xl"]).optional(),
  offlineSince: z.unknown().optional(),
  onlineSince: z.unknown().optional(),
  onRetry: z.unknown().refine((v) => v === void 0 || typeof v === "function", {
    message: "expected function or undefined"
  }).optional(),
  retryLabel: z.string().optional()
});
function toIso(v) {
  if (v == null) return void 0;
  if (v instanceof Date) return v.toISOString();
  if (typeof v === "number") return new Date(v).toISOString();
  return String(v);
}
var OfflineState = memo(function OfflineState2(props) {
  const parsed = OfflineStatePropsSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({
      lib: STATES_LIB,
      libToken: STATES_TOKEN,
      fn: "OfflineState",
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
  const {
    title,
    description,
    icon,
    illustration,
    actions,
    className,
    live = "polite",
    size = "md",
    offlineSince,
    onlineSince,
    onRetry,
    retryLabel = "Retry"
  } = props;
  if (onRetry !== void 0 && typeof onRetry !== "function") {
    try {
      warn({
        fn: "OfflineState",
        code: "STATES_INVALID_PROPS",
        message: "OfflineState received a non-function `onRetry`; built-in retry button will be suppressed.",
        prop: "onRetry",
        received: onRetry,
        expected: "a function () => void (or omit the prop)",
        dedupKey: "OfflineState:onRetry-not-a-function"
      });
    } catch {
    }
  }
  const isBackOnline = onlineSince != null;
  const resolvedTitle = title ?? (isBackOnline ? "Back online" : "You appear to be offline");
  const actionsArray = actions ? Array.isArray(actions) ? actions.filter((a) => a != null) : null : null;
  const renderedActions = actionsArray && actionsArray.length > 0 ? actionsArray.map((a, i) => /* @__PURE__ */ jsx(
    "span",
    {
      "data-testid": `offline-state-action-item-${i}`,
      className: "norkron-offline-state__action-item",
      children: a
    },
    i
  )) : actions;
  const safeOnRetry = typeof onRetry === "function" ? onRetry : void 0;
  const retryNode = !actions && safeOnRetry && !isBackOnline ? /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: safeOnRetry,
      "data-testid": "offline-state__retry",
      className: "norkron-offline-state__retry",
      children: retryLabel
    }
  ) : null;
  const finalActions = renderedActions ?? retryNode;
  let resolvedIllustration = null;
  if (typeof illustration === "string") {
    if (isStateIllustrationKey(illustration)) {
      resolvedIllustration = renderStateIllustration(illustration);
    } else {
      try {
        warn({
          fn: "OfflineState",
          code: "STATES_INVALID_PROPS",
          message: `OfflineState received unknown illustration key "${illustration}"; illustration slot will collapse.`,
          prop: "illustration",
          received: illustration,
          expected: "a known StateIllustration key or a ReactNode",
          dedupKey: `OfflineState:unknown-illustration:${illustration}`
        });
      } catch {
      }
    }
  } else if (illustration != null) {
    resolvedIllustration = illustration;
  }
  const rootClass = className ? `norkron-offline-state ${className}` : "norkron-offline-state";
  const liveProps = live === "off" ? null : { role: "status", "aria-live": live };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ...liveProps ?? {},
      className: rootClass,
      "data-testid": "offline-state",
      "data-norkron-lib": `@norkron/web-states@${LIB_VERSION}`,
      "data-states-variant": "offline",
      "data-states-has-action": finalActions ? "true" : "false",
      "data-size": size,
      "data-online": isBackOnline ? "true" : "false",
      "data-offline-since": toIso(offlineSince) ?? "",
      "data-online-since": toIso(onlineSince) ?? "",
      children: [
        resolvedIllustration ? /* @__PURE__ */ jsx(
          "div",
          {
            "data-testid": "offline-state-illustration",
            className: "norkron-offline-state__illustration",
            "aria-hidden": "true",
            children: resolvedIllustration
          }
        ) : illustration != null ? null : icon ? /* @__PURE__ */ jsx(
          "div",
          {
            "data-testid": "offline-state-icon",
            className: "norkron-offline-state__icon",
            "aria-hidden": "true",
            children: icon
          }
        ) : null,
        /* @__PURE__ */ jsx("h3", { className: "norkron-offline-state__title", children: resolvedTitle }),
        description ? /* @__PURE__ */ jsx("p", { className: "norkron-offline-state__description", children: description }) : null,
        finalActions ? /* @__PURE__ */ jsx(
          "div",
          {
            "data-testid": "offline-state-actions",
            className: "norkron-offline-state__actions",
            children: /* @__PURE__ */ jsx(ErrorBoundary, { fallback: null, children: finalActions })
          }
        ) : null
      ]
    }
  );
});
function useNavigatorOnline() {
  const initial = typeof navigator !== "undefined" ? navigator.onLine !== false : true;
  const [state, setState] = useState({ online: initial, offlineSince: void 0, onlineSince: void 0 });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onOffline = () => setState((s) => ({ ...s, online: false, offlineSince: /* @__PURE__ */ new Date() }));
    const onOnline = () => setState((s) => ({ ...s, online: true, onlineSince: /* @__PURE__ */ new Date() }));
    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, []);
  return state;
}
var MaintenanceStatePropsSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  icon: z.unknown().optional(),
  illustration: z.unknown().optional(),
  actions: z.unknown().optional(),
  className: z.string().optional(),
  live: z.enum(["polite", "assertive", "off"]).optional(),
  size: z.enum(["sm", "md", "lg", "xl"]).optional(),
  expectedReturn: z.unknown().optional()
});
function toIso2(v) {
  if (v == null) return void 0;
  if (v instanceof Date) return v.toISOString();
  if (typeof v === "number") return new Date(v).toISOString();
  return String(v);
}
var MaintenanceState = memo(function MaintenanceState2(props) {
  const parsed = MaintenanceStatePropsSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({
      lib: STATES_LIB,
      libToken: STATES_TOKEN,
      fn: "MaintenanceState",
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
  const {
    title = "We'll be right back",
    description,
    icon,
    illustration,
    actions,
    className,
    live = "polite",
    size = "md",
    expectedReturn
  } = props;
  const actionsArray = actions ? Array.isArray(actions) ? actions.filter((a) => a != null) : null : null;
  const renderedActions = actionsArray && actionsArray.length > 0 ? actionsArray.map((a, i) => /* @__PURE__ */ jsx(
    "span",
    {
      "data-testid": `maintenance-state-action-item-${i}`,
      className: "norkron-maintenance-state__action-item",
      children: a
    },
    i
  )) : actions;
  const rootClass = className ? `norkron-maintenance-state ${className}` : "norkron-maintenance-state";
  const liveProps = live === "off" ? null : { role: "status", "aria-live": live };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ...liveProps ?? {},
      className: rootClass,
      "data-testid": "maintenance-state",
      "data-norkron-lib": `@norkron/web-states@${LIB_VERSION}`,
      "data-states-variant": "maintenance",
      "data-states-has-action": renderedActions ? "true" : "false",
      "data-size": size,
      "data-expected-return": toIso2(expectedReturn) ?? "",
      children: [
        illustration ? /* @__PURE__ */ jsx(
          "div",
          {
            "data-testid": "maintenance-state-illustration",
            className: "norkron-maintenance-state__illustration",
            "aria-hidden": "true",
            children: illustration
          }
        ) : icon ? /* @__PURE__ */ jsx(
          "div",
          {
            "data-testid": "maintenance-state-icon",
            className: "norkron-maintenance-state__icon",
            "aria-hidden": "true",
            children: icon
          }
        ) : null,
        /* @__PURE__ */ jsx("h3", { className: "norkron-maintenance-state__title", children: title }),
        description ? /* @__PURE__ */ jsx("p", { className: "norkron-maintenance-state__description", children: description }) : null,
        renderedActions ? /* @__PURE__ */ jsx(
          "div",
          {
            "data-testid": "maintenance-state-actions",
            className: "norkron-maintenance-state__actions",
            children: /* @__PURE__ */ jsx(ErrorBoundary, { fallback: null, children: renderedActions })
          }
        ) : null
      ]
    }
  );
});
try {
  registerLazy(
    "@norkron/web-states",
    () => selfCheck
  );
} catch {
}

export { EmptyDataIllustration, EmptyFolderIllustration, EmptyInboxIllustration, EmptySearchIllustration, EmptyState, Error403Illustration, Error404Illustration, ErrorNetworkIllustration, ErrorServerIllustration, ErrorState, LIB_VERSION, LoadingState, MaintenanceState, MaintenanceToolsIllustration, OfflineCloudIllustration, OfflineState, STATE_ILLUSTRATION_KEYS, isStateIllustrationKey, renderStateIllustration, selfCheck, useNavigatorOnline };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map